import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';
import errorService from '@services/errorService';
import { useGetDevicesQuery } from '@store/api/deviceApi';
import { useCreateDeploymentMutation, type DeploymentKind } from '@store/api/deploymentsApi';
import DeviceTargetPicker from './DeviceTargetPicker';

const DEFAULT_TIMEOUT: Record<DeploymentKind, number> = {
  client: 360,
  dotnet: 300,
  dotnet_runtime: 3600,
  os_packages: 3600,
};

const WAITS_FOR_OFFLINE = 'How long the command waits for an offline device to pick it up';
const TIMEOUT_HELP: Record<DeploymentKind, string> = {
  client: 'How long the device has to install and report back',
  dotnet: 'Also the time the update script may run',
  dotnet_runtime: WAITS_FOR_OFFLINE,
  os_packages: WAITS_FOR_OFFLINE,
};

/** A 422 for unknown/unverified devices names them; say which, not raw JSON. */
const deployErrorMessage = (err: unknown): string => {
  const detail = (err as { data?: { detail?: { device_ids?: string[] } } })?.data?.detail;
  if (detail?.device_ids) {
    return `These devices are unknown or not verified: ${detail.device_ids.join(', ')}`;
  }
  return errorService.toUserMessage(err);
};

interface DeployDialogProps {
  kind: DeploymentKind;
  /** The version to deploy; the dialog is open while this is set. Callers key
   *  the dialog on it, so every opening starts from a clean slate. */
  version: string | null;
  onClose: () => void;
  /** Explains what the update does on the device. */
  notice: React.ReactNode;
  /** Defaults to "Deploy {version}". */
  title?: string;
}

/** Pick Devices and create one Deployment, then open its progress page. */
const DeployDialog: React.FC<DeployDialogProps> = ({ kind, version, onClose, notice, title }) => {
  const navigate = useNavigate();
  const { data: devicesData } = useGetDevicesQuery({ pageSize: 999 }, { skip: !version });
  const [createDeployment, { isLoading: deploying }] = useCreateDeploymentMutation();

  const [selected, setSelected] = useState<string[]>([]);
  const [timeout, setTimeoutSeconds] = useState(DEFAULT_TIMEOUT[kind]);
  const [rollbackOnFailure, setRollbackOnFailure] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const timeoutValid = timeout >= 60 && timeout <= 3600;

  const handleDeploy = async () => {
    if (!version) return;
    try {
      setError(null);
      const deployment = await createDeployment({
        kind,
        version,
        device_ids: selected,
        options: {
          timeout,
          ...(kind === 'client' ? { rollback_on_failure: rollbackOnFailure } : {}),
        },
      }).unwrap();
      onClose();
      navigate(`/deployments/${deployment.id}`);
    } catch (err) {
      setError(deployErrorMessage(err));
    }
  };

  return (
    <Dialog open={!!version} onClose={() => !deploying && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{title ?? `Deploy ${version}`}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {notice}
          {error && <Alert severity="error">{error}</Alert>}
          {version && (
            <DeviceTargetPicker
              kind={kind}
              targetVersion={version}
              devices={devicesData?.data ?? []}
              selected={selected}
              onChange={setSelected}
              disabled={deploying}
            />
          )}
          <TextField
            label="Timeout per device (seconds)"
            type="number"
            value={timeout}
            onChange={(e) => setTimeoutSeconds(Number(e.target.value))}
            error={!timeoutValid}
            helperText={
              timeoutValid ? TIMEOUT_HELP[kind] : 'Between 60 and 3600 seconds'
            }
            disabled={deploying}
            fullWidth
            slotProps={{
              htmlInput: { min: 60, max: 3600 }
            }}
          />
          {kind === 'client' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={rollbackOnFailure}
                  onChange={(e) => setRollbackOnFailure(e.target.checked)}
                  disabled={deploying}
                />
              }
              label="Rollback on failure"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deploying}>
          Cancel
        </Button>
        <Button
          onClick={handleDeploy}
          variant="contained"
          disabled={selected.length === 0 || deploying || !timeoutValid}
          startIcon={deploying ? <CircularProgress size={16} /> : undefined}
        >
          {deploying ? 'Deploying...' : `Deploy to ${selected.length} device(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeployDialog;
